import dayjs from 'dayjs';
import 'dayjs/locale/it';
import i18next, { type i18n as I18n, type Resource } from 'i18next';
import { DEFAULT_LOCALE, isSupportedLocale, type Locale, SUPPORTED_LOCALES } from './config';
import { PSEUDO_POST_PROCESSOR, pseudoPostProcessor } from './pseudo';
import { DEFAULT_NAMESPACE, sharedResources } from './resources';

/**
 * The one i18next instance both apps translate with.
 *
 * A module-level instance rather than one built inside a provider, because a good share of what
 * gets translated never renders: toasts fired from hooks, Zod messages, the `Accept-Language` the
 * REST fetchers send. All of those read it directly; components read it through `react-i18next`,
 * which each app mounts with `<I18nextProvider i18n={i18n}>`.
 *
 * `react-i18next` stays out of this package on purpose. Each app owns its React bindings, so the
 * provider and the hooks are always the same copy, whatever peers this package resolves with.
 */
export const i18n: I18n = i18next.createInstance();

/** An app's own namespaces, per locale: `{ en: { trips: {...} }, it: { trips: {...} } }`. */
export type AppResources = Partial<Record<Locale, Record<string, object>>>;

type InitI18nOptions = {
  locale: Locale;
  resources?: AppResources;
  /**
   * Shows every translated string in the pseudo-locale (see `pseudo.ts`), for a dev build to spot
   * what is still hardcoded. Never on in a release build: each app decides from its own dev flag.
   */
  pseudo?: boolean;
};

const mergeResources = (appResources: AppResources): Resource =>
  Object.fromEntries(
    SUPPORTED_LOCALES.map((locale) => [locale, { ...sharedResources[locale], ...appResources[locale] }]),
  );

/**
 * Keeps dayjs speaking the same language as the UI, so a user's own `date_format` renders Italian
 * month and weekday names in Italian without any formatting call having to know about it. Global
 * because dayjs's locale is: the alternative is passing a locale to several hundred `format` calls.
 */
const applyDayjsLocale = (language: string) => {
  dayjs.locale(isSupportedLocale(language) ? language : DEFAULT_LOCALE);
};

/**
 * Starts the shared instance in `locale` with the shared namespaces plus the app's own.
 *
 * Synchronous in effect: every resource is bundled, and `initAsync: false` makes i18next load them
 * in the same tick, so `t()` answers in the right language as soon as this returns — which is what
 * lets an app call it at module scope and render its first frame translated.
 *
 * Safe to call again (tests do, once per file): a second call adds the given namespaces and switches
 * the language instead of initialising twice.
 */
export const initI18n = ({ locale, resources = {}, pseudo = false }: InitI18nOptions): I18n => {
  const merged = mergeResources(resources);
  const postProcess = pseudo ? [PSEUDO_POST_PROCESSOR] : false;

  if (i18n.isInitialized) {
    i18n.options.postProcess = postProcess;

    for (const [language, namespaces] of Object.entries(merged)) {
      for (const [namespace, bundle] of Object.entries(namespaces)) {
        i18n.addResourceBundle(language, namespace, bundle, true, true);
      }
    }

    void i18n.changeLanguage(locale);

    return i18n;
  }

  i18n.on('languageChanged', applyDayjsLocale);

  void i18n.use(pseudoPostProcessor).init({
    lng: locale,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    resources: merged,
    ns: Object.keys(merged[DEFAULT_LOCALE] ?? {}),
    defaultNS: DEFAULT_NAMESPACE,
    initAsync: false,
    postProcess,
    // React escapes what it renders. Escaping here as well would show `&amp;` in a boat name.
    interpolation: { escapeValue: false },
    // Every resource is in memory, so there is never anything to suspend for. Mobile screens sit
    // inside Suspense boundaries that are about data; a translation must never trip one.
    react: { useSuspense: false },
  });

  return i18n;
};

/**
 * The locale the UI is in right now, for code that formats outside React — `Intl` formatters, the
 * `Accept-Language` header. English before `initI18n` has run, which is also what it settles on.
 */
export const getActiveLocale = (): Locale => {
  const language = i18n.resolvedLanguage ?? i18n.language;

  return isSupportedLocale(language) ? language : DEFAULT_LOCALE;
};
